import { useState, useEffect } from 'react';
import { teamInvitationApi } from '../api/teamInvitations';
import { useUser } from '../context/UserContext';

/**
 * Hook for managing team invitations
 * Handles fetching user invitations and responding to them
 */
export const useTeamInvitations = () => {
  const { userProfile } = useUser();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user's pending invitations
  const fetchInvitations = async () => {
    if (!userProfile?._id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await teamInvitationApi.getUserInvitations();
      if (response.success) {
        setInvitations(response.invitations || []);
      } else {
        setError(response.message || 'Failed to fetch invitations');
      }
    } catch (err) {
      setError('Error fetching invitations');
      console.error('Fetch invitations error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Respond to an invitation
  const respondToInvitation = async (invitationId, response, message = '') => {
    setLoading(true);
    setError(null);

    try {
      const result = await teamInvitationApi.respondToInvitation(invitationId, response, message);
      
      if (result.success) {
        // Remove the invitation from the list
        setInvitations(prev => prev.filter(inv => inv._id !== invitationId));
        return { success: true, data: result };
      } else {
        setError(result.message || 'Failed to respond to invitation');
        return { success: false, error: result.message };
      }
    } catch (err) {
      setError('Error responding to invitation');
      console.error('Respond to invitation error:', err);
      return { success: false, error: 'Error responding to invitation' };
    } finally {
      setLoading(false);
    }
  };

  // Cancel an invitation (for team managers)
  const cancelInvitation = async (invitationId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await teamInvitationApi.cancelInvitation(invitationId);
      
      if (result.success) {
        // Remove the invitation from the list
        setInvitations(prev => prev.filter(inv => inv._id !== invitationId));
        return { success: true };
      } else {
        setError(result.message || 'Failed to cancel invitation');
        return { success: false, error: result.message };
      }
    } catch (err) {
      setError('Error canceling invitation');
      console.error('Cancel invitation error:', err);
      return { success: false, error: 'Error canceling invitation' };
    } finally {
      setLoading(false);
    }
  };

  // Get pending invitations count
  const getPendingCount = () => {
    return invitations.filter(inv => inv.status === 'pending').length;
  };

  // Check if user has pending invitations
  const hasPendingInvitations = () => {
    return getPendingCount() > 0;
  };

  // Get invitations by status
  const getInvitationsByStatus = (status) => {
    return invitations.filter(inv => inv.status === status);
  };

  useEffect(() => {
    fetchInvitations();
  }, [userProfile?._id]);

  return {
    invitations,
    loading,
    error,
    fetchInvitations,
    respondToInvitation,
    cancelInvitation,
    getPendingCount,
    hasPendingInvitations,
    getInvitationsByStatus,
    setError
  };
};

export default useTeamInvitations;
